/**
 * Trusscore Material Estimator
 *
 * Main Application Component with Routing
 *
 * On startup, attempts to load product data from the API.
 * Falls back to hardcoded data if the API is unreachable.
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { EstimatorProvider } from './context/EstimatorContext';
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { AppSettingsProvider } from './context/AppSettingsContext';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { EstimatorWizard } from './components/estimator/EstimatorWizard';
import { SharedEstimate } from './pages/SharedEstimate';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminEstimates } from './pages/admin/AdminEstimates';
import { AdminAnalytics } from './pages/admin/AdminAnalytics';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminShares } from './pages/admin/AdminShares';
import { AdminPartners } from './pages/admin/AdminPartners';
import { AdminEmailTemplates } from './pages/admin/AdminEmailTemplates';
import { SharedWithUs } from './pages/SharedWithUs';
import { AdminProducts } from './pages/admin/AdminProducts';
import { RetailersWorkspace } from './pages/admin/retailers/RetailersWorkspace';
import { AdminAccounts } from './pages/admin/AdminAccounts';
import { AdminFieldDefs } from './pages/admin/AdminFieldDefs';
import { AdminColumnLayout } from './pages/admin/AdminColumnLayout';
import { AdminLocations } from './pages/admin/AdminLocations';
import { AdminUserCategories } from './pages/admin/AdminUserCategories';
import { AdminTranslations } from './pages/admin/AdminTranslations';
import { AdminLocales } from './pages/admin/AdminLocales';
import { AdminCustomTables } from './pages/admin/AdminCustomTables';
import { AdminActionButtons } from './pages/admin/AdminActionButtons';
import { AdminLocationMaps } from './pages/admin/AdminLocationMaps';
import { AdminOutputSections } from './pages/admin/AdminOutputSections';
import { AdminSiteContent } from './pages/admin/AdminSiteContent';
import { RequireAdmin, RequireSuperAdmin } from './pages/admin/RequireAdmin';
import { Navigate } from 'react-router-dom';
import { RequireAuth } from './pages/RequireAuth';
import { Account } from './pages/Account';
import { RetailerJoin } from './pages/RetailerJoin';
import { RequireRetailerMember, RequireManager } from './pages/retaileradmin/RequireRetailerMember';
import { RetailerUsers } from './pages/retaileradmin/RetailerUsers';
import { RetailerLocations } from './pages/retaileradmin/RetailerLocations';
import { RetailerPriceBookGate } from './components/RetailerPriceBookGate';
import { RetailerAdminLayout } from './pages/retaileradmin/RetailerAdminLayout';
import { RetailerDashboard } from './pages/retaileradmin/RetailerDashboard';
import { RetailerEstimates } from './pages/retaileradmin/RetailerEstimates';
import { RetailerAnalytics } from './pages/retaileradmin/RetailerAnalytics';
import { MyEstimates } from './pages/MyEstimates';
import { MyInformation } from './components/account/MyInformation';
import { DevToolsOverlay } from './components/dev/DevToolsOverlay';
import { ToastProvider } from './context/ToastContext';
import { SharedEstimateViewProvider } from './context/SharedEstimateViewContext';
import { PartnerBrandingProvider, usePartnerBranding } from './context/PartnerBrandingContext';
import { PartnerBetaBanner } from './components/layout/PartnerBetaBanner';
import { SplashOverlay } from './components/ui/SplashOverlay';
import { TutorialVideoLightbox } from './components/ui/TutorialVideoLightbox';
import { AuthModal } from './components/auth/AuthModal';
import { LogoutConfirmModal } from './components/auth/LogoutConfirmModal';
import { AuthToast } from './components/auth/AuthToast';
import { GleapChat } from './components/GleapChat';
import { AccountSecurity } from './components/account/AccountSecurity';
import { GlobalToast } from './components/ui/GlobalToast';
import { useTranslation } from 'react-i18next';
import { loadProducts, getLoadedRetailerId } from './data/productTable';
import { setTrackingContext } from './utils/gtmTracking';
import { SiteExperienceProvider } from './context/SiteExperienceContext';
import { LanguageProvider } from './context/LanguageContext';

/**
 * Loads product data for the resolved retailer context (partner subdomain →
 * that retailer; apex/dev → Trusscore retailer #1) and gates the app behind it.
 * Runs inside PartnerBrandingProvider so the partner id is available — this is
 * what makes a partner site's custom fields + pricing load live in the wizard.
 */
const ProductsBootGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { partner, loading } = usePartnerBranding();
  // Product names, custom-field headings, action-button labels and custom-table
  // titles all arrive translated in this one payload, so the locale is a real
  // input to it — switching language re-hydrates rather than re-rendering.
  const { t, i18n } = useTranslation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (loading) return; // wait for partner resolution before choosing retailer
    let cancelled = false;
    setReady(false);
    loadProducts(partner?.id ?? 1, true)
      .catch(() => false)
      .then(() => {
        // Tell analytics which price book is actually in use. Retailer 1 is
        // Trusscore MSRP; null means the API was unreachable and the static
        // fallback table is showing.
        const loadedId = getLoadedRetailerId();
        setTrackingContext({
          retailerId: loadedId,
          priceBook: loadedId === null ? 'fallback' : loadedId === 1 ? 'msrp' : 'retailer',
        });
      })
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, [loading, partner?.id, i18n.language]);

  if (!ready) {
    return (
      <div className="app" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #1a1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#6b7280', fontSize: 14 }}>{t('common:loadingProducts')}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <PartnerBrandingProvider>
      {/* Language sits between partner resolution and the products gate on
          purpose: it needs the retailer's default_locale (resolved above), and
          product names are translated server-side, so the locale must be
          settled before loadProducts() runs below. */}
      <LanguageProvider>
      <ProductsBootGate>
      <SiteExperienceProvider>
      <AppSettingsProvider>
      <AuthProvider>
      <AdminAuthProvider>
      <ToastProvider>
      <EstimatorProvider>
      <SharedEstimateViewProvider>
        <div className="app">
          <PartnerBetaBanner />
          <Header />
          {/* SplashOverlay lives at app root so the Header's "About" menu
              (which fires a `show-splash` event) works regardless of route.
              Auto-show is internally gated to the wizard route only. */}
          <SplashOverlay />
          {/* Mounted at app root so the Tutorial nav entries can dispatch
              `show-tutorial-video` from any route. */}
          <TutorialVideoLightbox />
          {/* Native in-app login modal — only renders/opens when the
              nativeAuthEnabled toggle is on; otherwise login() redirects. */}
          <AuthModal />
          {/* Native in-app logout confirmation — only opens in nativeAuthEnabled mode. */}
          <LogoutConfirmModal />
          {/* Centered auto-dismiss "signed in" confirmation after a native login. */}
          <AuthToast />
          {/* Gleap chat launcher — gated by the Integrations → Gleap admin
              settings; renders nothing itself. */}
          <GleapChat />
          <main className="main-content">
            <RetailerPriceBookGate>
            <Routes>
              <Route path="/" element={<EstimatorWizard />} />
              <Route path="/estimate/:id" element={<SharedEstimate />} />
              <Route path="/mtl/:id" element={<SharedEstimate />} />
              {/* Retailer-staff sign-up: reachable by URL only (Pardot invitations), no nav entry. */}
              <Route path="/retailers/join" element={<RetailerJoin />} />
              <Route path="/account" element={<RequireAuth><Account /></RequireAuth>}>
                <Route index element={<MyEstimates />} />
                <Route path="inbox" element={<SharedWithUs />} />
                <Route path="profile" element={<MyInformation />} />
                <Route path="security" element={<AccountSecurity />} />
              </Route>
              {/* Retailer staff area — approved retailer_members only (server-gated). */}
              <Route
                path="/retaileradmin"
                element={<RequireRetailerMember><RetailerAdminLayout /></RequireRetailerMember>}
              >
                <Route index element={<RetailerDashboard />} />
                <Route path="estimates" element={<RetailerEstimates />} />
                <Route path="analytics" element={<RetailerAnalytics />} />
                <Route path="locations" element={<RetailerLocations />} />
                <Route path="users" element={<RequireManager><RetailerUsers /></RequireManager>} />
              </Route>
              <Route
                path="/admin"
                element={<RequireAdmin><AdminDashboard /></RequireAdmin>}
              >
                <Route path="estimates" element={<AdminEstimates />} />
                <Route path="shares" element={<AdminShares />} />
                {/* One Retailers workspace: tree · locations · people. Old tab paths redirect. */}
                <Route path="retailers/locations" element={<Navigate to="/admin/retailers/unassigned" replace />} />
                <Route path="retailers/users" element={<Navigate to="/admin/retailers" replace />} />
                <Route path="retailers/invites" element={<Navigate to="/admin/retailers" replace />} />
                <Route path="retailers/:nodeId" element={<RetailersWorkspace />} />
                <Route path="installers" element={<RequireSuperAdmin><AdminPartners kind="installer" /></RequireSuperAdmin>} />
                <Route path="accounts" element={<RequireSuperAdmin><AdminAccounts /></RequireSuperAdmin>} />
                <Route path="retailer-locations" element={<Navigate to="/admin/retailers/unassigned" replace />} />
                <Route
                  path="email-templates"
                  element={
                    <RequireSuperAdmin>
                      <AdminEmailTemplates />
                    </RequireSuperAdmin>
                  }
                />
                <Route path="retailers" element={<RetailersWorkspace />} />
                <Route path="retailer-members" element={<Navigate to="/admin/retailers/users" replace />} />
                <Route path="products" element={<AdminProducts />} />
                <Route
                  path="product-fields"
                  element={
                    <RequireSuperAdmin>
                      <AdminFieldDefs />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="column-layout"
                  element={
                    <RequireSuperAdmin>
                      <AdminColumnLayout />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="custom-tables"
                  element={
                    <RequireSuperAdmin>
                      <AdminCustomTables />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="action-buttons"
                  element={
                    <RequireSuperAdmin>
                      <AdminActionButtons />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="maps"
                  element={
                    <RequireSuperAdmin>
                      <AdminLocationMaps />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="output-sections"
                  element={
                    <RequireSuperAdmin>
                      <AdminOutputSections />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="site-content"
                  element={
                    <RequireSuperAdmin>
                      <AdminSiteContent />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="locations"
                  element={
                    <RequireSuperAdmin>
                      <AdminLocations />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="user-categories"
                  element={
                    <RequireSuperAdmin>
                      <AdminUserCategories />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="locales"
                  element={
                    <RequireSuperAdmin>
                      <AdminLocales />
                    </RequireSuperAdmin>
                  }
                />
                <Route
                  path="translations"
                  element={
                    <RequireSuperAdmin>
                      <AdminTranslations />
                    </RequireSuperAdmin>
                  }
                />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route
                  path="settings"
                  element={
                    <RequireSuperAdmin>
                      <AdminSettings />
                    </RequireSuperAdmin>
                  }
                />
              </Route>
            </Routes>
            </RetailerPriceBookGate>
          </main>
          <Footer />
        </div>
        <DevToolsOverlay />
        <GlobalToast />
      </SharedEstimateViewProvider>
      </EstimatorProvider>
      </ToastProvider>
      </AdminAuthProvider>
      </AuthProvider>
      </AppSettingsProvider>
      </SiteExperienceProvider>
      </ProductsBootGate>
      </LanguageProvider>
      </PartnerBrandingProvider>
    </Router>
  );
};

export default App;
